import type { StoreProductSummary } from "schemas/product";

import * as React from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { RIGHT_BAR_WIDTH } from "@v1/components/editor/constants";
import { ProductSelectionCombobox } from "@v1/components/editor/page/ProductSelectionCombobox";
import {
  productSummaryRequestLimits,
  useAssignedProductsSummary,
  useFirstNProductsSummaryOrPlaceholderProducts,
  useTemplateEditorProductSummary,
} from "@v1/hooks/useProductsSummary";
import useRightBarVisibility from "@v1/hooks/useRightBarVisibility";
import {
  selectAllComponentIdsWithTemplateProductEqualsStates,
  selectDraftElementId,
  selectDraftElementTemplateProducts,
  selectDraftElementType,
  selectProjectId,
  setComponentVariant,
} from "@v1/reducers/core-reducer";
import {
  selectTemplateEditorProduct,
  setTemplateEditorProduct,
} from "@v1/reducers/template-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fakeProducts } from "replo-runtime/store/utils/fakeProducts";

const TemplateProductSelector = () => {
  const draftElementType = useEditorSelector(selectDraftElementType);
  const draftElementId = useEditorSelector(selectDraftElementId);
  const isRightBarVisible = useRightBarVisibility();
  const projectId = useEditorSelector(selectProjectId);
  const dispatch = useEditorDispatch();
  const templateEditorProduct = useEditorSelector(selectTemplateEditorProduct);
  const assignedProductIds = useEditorSelector(
    selectDraftElementTemplateProducts,
  );

  const { isLoading: isLoadingProductsToLoopOver } = useProductsToLoopOver();
  const { isLoading: isLoadingAssignedProductsSummary } =
    useAssignedProductsSummary();
  useUpdateTemplateEditorProduct();
  const {
    templateEditorProductSummary,
    isLoading: isLoadingTemplateEditorProductSummary,
  } = useTemplateEditorProductSummary();

  const handleArrowButtons = useArrowButtonHandler();

  const anchorRef = React.useRef(null);

  const resetTemplateProductEqualsStates =
    useResetTemplateProductEqualsStates();

  if (
    draftElementType !== "shopifyProductTemplate" ||
    !draftElementId ||
    !projectId ||
    !templateEditorProduct
  ) {
    return null;
  }

  const isLoading =
    isLoadingProductsToLoopOver ||
    isLoadingAssignedProductsSummary ||
    isLoadingTemplateEditorProductSummary;

  return (
    <div
      className="fixed"
      style={{
        right: isRightBarVisible ? RIGHT_BAR_WIDTH + 14 : 14,
        top: 72,
      }}
    >
      <div className="box-border flex h-16 w-64 flex-col justify-between rounded-sm bg-white px-3 py-2 shadow-lg">
        <div className="text-default text-xs font-medium">
          Editing Product Template With
        </div>
        {isLoading && (
          <div className="absolute bottom-2 box-border h-[24px] w-[256px] rounded-sm bg-white">
            <Skeleton className="h-[24px] w-[256px]" />
          </div>
        )}
        {!isLoading && templateEditorProductSummary && (
          <div
            className="flex max-w-full items-start justify-between"
            ref={anchorRef}
          >
            {templateEditorProductSummary && (
              <ProductSelectionCombobox
                size="sm"
                value={{
                  productId: templateEditorProductSummary.id,
                  title: templateEditorProductSummary.title,
                  variantId: undefined,
                }}
                onChange={(newProductRef) => {
                  if (newProductRef) {
                    dispatch(
                      setTemplateEditorProduct({
                        productId: newProductRef.productId,
                      }),
                    );
                    resetTemplateProductEqualsStates();
                  }
                }}
                side="left"
                sideOffset={20}
              />
            )}
            <div className="ml-2 flex gap-1.5">
              <DeprecatedIconButton
                icon={<ChevronLeft size={12} className="text-muted" />}
                variant="secondary"
                size="sm"
                tooltipText="Previous Product"
                onClick={() => {
                  handleArrowButtons("decrement");
                }}
                disabled={assignedProductIds?.length === 1}
              />
              <DeprecatedIconButton
                icon={<ChevronRight size={12} className="text-muted" />}
                variant="secondary"
                size="sm"
                tooltipText="Next Product"
                onClick={() => {
                  handleArrowButtons("increment");
                }}
                disabled={assignedProductIds?.length === 1}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const useProductsToLoopOver = (): {
  productsSummary: StoreProductSummary[] | undefined;
  isLoading: boolean;
  productListType: "assigned" | "all" | "placeholder" | undefined;
} => {
  const {
    productsSummary: firstNOrPlaceholderProductsSummary,
    isLoading: isLoadingFirstNProducts,
    isPlaceholderProducts,
  } = useFirstNProductsSummaryOrPlaceholderProducts(
    productSummaryRequestLimits.default,
  );

  if (isLoadingFirstNProducts) {
    return {
      productsSummary: [],
      isLoading: true,
      productListType: undefined,
    };
  }

  return {
    productsSummary: firstNOrPlaceholderProductsSummary,
    isLoading: false,
    productListType: isPlaceholderProducts ? "placeholder" : "all",
  };
};

const isPlaceholderProduct = (productId: number) => {
  return fakeProducts.some(
    (fakeProduct) => Number(fakeProduct.id) === productId,
  );
};

// Note (Evan, 2023-10-25): This hook handles updating the template editor product when we change stores/elements/etc.
const useUpdateTemplateEditorProduct = () => {
  const templateEditorProduct = useEditorSelector(selectTemplateEditorProduct);

  const {
    productsSummary: productsToLoopOver,
    productListType,
    isLoading: isLoadingProductsToLoopOver,
  } = useProductsToLoopOver();

  const {
    templateEditorProductSummary,
    isLoading: isLoadingTemplateEditorProductSummary,
  } = useTemplateEditorProductSummary();

  const dispatch = useEditorDispatch();

  const resetTemplateProductEqualsStates =
    useResetTemplateProductEqualsStates();

  // Note (Evan, 2023-10-25): "One effect to rule them all" – this replaces the complex logic of resetting
  // [ 1) when the canvas loads, 2) when the draft element changes, and 3) when the assigned products change such that the
  // current product is no longer assigned ] with a single effect that resets the template editor product whenever it is
  // invalid (when it's not in the list of possible template products).
  //
  // We determine this based on the productListType (the strategy of what kinds of products are shown)
  // If we're showing all products, we should reset when the /summary endpoint can't find the current product.
  // If we're showing placeholder products, we should reset when the current product isn't a placeholder product.
  React.useEffect(() => {
    if (
      !isLoadingProductsToLoopOver &&
      productsToLoopOver &&
      !isLoadingTemplateEditorProductSummary
    ) {
      const shouldReset = (() => {
        if (!templateEditorProduct) {
          return true;
        }
        if (productListType === "all" && !templateEditorProductSummary) {
          return true;
        }
        if (
          productListType === "placeholder" &&
          !isPlaceholderProduct(Number(templateEditorProduct.productId))
        ) {
          return true;
        }
        return false;
      })();
      if (shouldReset) {
        const newProductSummary = productsToLoopOver[0];
        // Note (Evan, 2023-10-25): We know productIdsToLoopOver[0] will always be defined because we're using useProductsSummaryOrPlaceholderProducts,
        // so we're guaranteed to at least have placeholder products, but we put a check here to make Typescript (and Gabe) happy.
        if (newProductSummary) {
          dispatch(
            setTemplateEditorProduct({ productId: newProductSummary.id }),
          );
          resetTemplateProductEqualsStates();
        }
      }
    }
  }, [
    isLoadingProductsToLoopOver,
    productsToLoopOver,
    isLoadingTemplateEditorProductSummary,
    templateEditorProduct,
    productListType,
    templateEditorProductSummary,
    dispatch,
    resetTemplateProductEqualsStates,
  ]);
  return templateEditorProduct;
};

const useArrowButtonHandler = (): ((
  actionType: "increment" | "decrement",
) => void) => {
  const dispatch = useEditorDispatch();
  const templateEditorProduct = useEditorSelector(selectTemplateEditorProduct);
  const { productsSummary } = useProductsToLoopOver();
  const resetTemplateProductEqualsStates =
    useResetTemplateProductEqualsStates();

  return (actionType: "increment" | "decrement") => {
    if (!productsSummary || !templateEditorProduct) {
      return;
    }

    const productIdsToLoopOver = productsSummary.map((productSummary) => {
      return Number(productSummary.id);
    });

    const currentIndex = productIdsToLoopOver.findIndex(
      (productId) => productId === Number(templateEditorProduct.productId),
    );

    const newIndex =
      actionType === "increment"
        ? (currentIndex + 1) % productIdsToLoopOver.length
        : (currentIndex + productIdsToLoopOver.length - 1) %
          productIdsToLoopOver.length;

    const newProduct = { productId: productIdsToLoopOver[newIndex] };
    // Note (Evan, 2023-10-12):  Typescript isn't smart enough to know that the
    // modulos ensure that newIndex will be <= products.length, so we expect an
    // error here
    // @ts-expect-error
    dispatch(setTemplateEditorProduct(newProduct));
    resetTemplateProductEqualsStates();
  };
};

// Note (Evan, 2023-11-16): Returns a function that looks for all components which have a "product equals" state and
// removes their entry from componentIdToDraftVariantId. The reasoning here is that changing the template product should
// cause states which depend on the template product to change.
const useResetTemplateProductEqualsStates = () => {
  const dispatch = useEditorDispatch();
  const ids = useEditorSelector(
    selectAllComponentIdsWithTemplateProductEqualsStates,
  );
  return () => {
    ids.forEach((componentId) => {
      dispatch(
        setComponentVariant({
          componentId,
          variantId: undefined,
        }),
      );
    });
  };
};

const TemplateProductSelectorWrapper = () => {
  const elementType = useEditorSelector(selectDraftElementType);
  return elementType === "shopifyProductTemplate" ? (
    <TemplateProductSelector />
  ) : null;
};

export default TemplateProductSelectorWrapper;
